import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Search, FileSpreadsheet, Download, ArrowUpCircle, History, Undo2 } from "lucide-react";
import { toast } from "sonner";
import api, { apiError, formatRupiah } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ConfirmDialog } from "@/components/ConfirmDialog";

const emptyForm = { nisn: "", name: "", class_name: "", parent_phone: "" };
const BULAN = ["", "Jan", "Feb", "Mar", "Apr", "Mei", "Jun", "Jul", "Agu", "Sep", "Okt", "Nov", "Des"];

export default function Students() {
  const { user, schoolId } = useAuth();
  const canEdit = ["super_admin", "bendahara"].includes(user.role);
  const [students, setStudents] = useState(null);
  const [needsSchool, setNeedsSchool] = useState(false);
  const [search, setSearch] = useState("");
  const [classFilter, setClassFilter] = useState("semua");
  const [showAlumni, setShowAlumni] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [promoteOpen, setPromoteOpen] = useState(false);
  const [finalGrade, setFinalGrade] = useState(6);
  const [promoting, setPromoting] = useState(false);
  const [arrears, setArrears] = useState({});
  const [historyStudent, setHistoryStudent] = useState(null);
  const [historyData, setHistoryData] = useState(null);
  const [restoring, setRestoring] = useState(null);

  const handlePromote = async () => {
    setPromoting(true);
    try {
      const r = await api.post("/students/promote", { final_grade: finalGrade });
      toast.success(`Kenaikan kelas selesai: ${r.data.promoted} siswa naik, ${r.data.graduated} lulus${r.data.skipped?.length ? `, ${r.data.skipped.length} dilewati (kelas tidak dikenali)` : ""}`);
      setPromoteOpen(false);
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setPromoting(false);
    }
  };

  const handleImport = async (e) => {
    e.preventDefault();
    if (!importFile) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", importFile);
      const r = await api.post("/students/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setImportResult(r.data);
      toast.success(`${r.data.imported} siswa berhasil diimpor`);
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setImporting(false);
    }
  };

  const handleTemplate = async () => {
    try {
      const res = await api.get("/students/import-template", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "format_impor_siswa.xlsx";
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error("Gagal mengunduh format");
    }
  };

  const load = () => {
    setNeedsSchool(false);
    api.get(showAlumni ? "/students?alumni=1" : "/students")
      .then((r) => setStudents(r.data))
      .catch((e) => {
        if (e.response?.status === 400) setNeedsSchool(true);
        else {
          setStudents([]);
          toast.error(apiError(e));
        }
      });
    if (showAlumni) {
      api.get("/reminders/unpaid")
        .then((r) => {
          const map = {};
          r.data.forEach((u) => { map[u.student_id] = u; });
          setArrears(map);
        })
        .catch(() => setArrears({}));
    } else {
      setArrears({});
    }
  };

  useEffect(() => {
    setStudents(null);
    if (user.role === "super_admin" && !schoolId) {
      setNeedsSchool(true);
      setStudents([]);
      return;
    }
    load();
  }, [schoolId, showAlumni]);

  const openAdd = () => {
    setEditing(null);
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const openEdit = (s) => {
    setEditing(s);
    setForm({ nisn: s.nisn, name: s.name, class_name: s.class_name, parent_phone: s.parent_phone || "" });
    setDialogOpen(true);
  };

  const openHistory = async (s) => {
    setHistoryStudent(s);
    setHistoryData(null);
    try {
      const [spp, sav, tgk] = await Promise.all([
        api.get(`/spp/history?student_id=${s.id}`),
        api.get(`/savings/history?student_id=${s.id}`),
        api.get(`/students/${s.id}/tunggakan`),
      ]);
      setHistoryData({ spp: spp.data, savings: sav.data, tunggakan: tgk.data });
    } catch (err) {
      toast.error(apiError(err));
      setHistoryStudent(null);
    }
  };

  const handleRestore = async () => {
    try {
      await api.post(`/students/${restoring.id}/restore`);
      toast.success(`${restoring.name} dikembalikan ke daftar siswa aktif`);
      setRestoring(null);
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/students/${editing.id}`, form);
        toast.success("Data siswa diperbarui");
      } else {
        await api.post("/students", form);
        toast.success("Siswa baru ditambahkan");
      }
      setDialogOpen(false);
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/students/${deleting.id}`);
      toast.success("Siswa dihapus");
      setDeleting(null);
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  if (needsSchool) {
    return (
      <div className="rounded-xl border border-dashed border-slate-300 bg-white p-12 text-center" data-testid="students-need-school">
        <p className="font-heading text-lg font-semibold text-slate-800">Pilih sekolah terlebih dahulu</p>
        <p className="mt-1 text-sm text-slate-500">Gunakan pemilih sekolah di pojok kiri atas.</p>
      </div>
    );
  }

  const classes = [...new Set((students || []).map((s) => s.class_name))].sort();
  const classCounters = {};
  const numMap = {};
  (students || []).forEach((s, i) => {
    classCounters[s.class_name] = (classCounters[s.class_name] || 0) + 1;
    numMap[s.id] = { no: i + 1, noKelas: classCounters[s.class_name] };
  });
  const filtered = (students || []).filter(
    (s) =>
      (classFilter === "semua" || s.class_name === classFilter) &&
      (s.name.toLowerCase().includes(search.toLowerCase()) ||
        s.nisn.toLowerCase().includes(search.toLowerCase()))
  );
  const showActions = canEdit || showAlumni;

  return (
    <div className="space-y-6" data-testid="students-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">Data Siswa</h1>
          <p className="mt-1 text-sm text-slate-500">
            {showAlumni
              ? "Arsip siswa lulus — riwayat keuangan dan tunggakannya tetap tersimpan."
              : "Kelola data siswa di sekolah Anda."}
          </p>
        </div>
        {canEdit && !showAlumni && (
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => { setImportResult(null); setImportFile(null); setImportOpen(true); }} data-testid="import-students-button" className="gap-2 rounded-md">
              <FileSpreadsheet className="h-4 w-4" strokeWidth={1.5} /> Impor Excel
            </Button>
            <Button onClick={openAdd} data-testid="add-student-button" className="gap-2 rounded-md bg-[#0060AF] hover:bg-[#004B87] active:scale-[0.98]">
              <Plus className="h-4 w-4" strokeWidth={1.5} /> Tambah Siswa
            </Button>
            <Button variant="outline" onClick={() => setPromoteOpen(true)} data-testid="promote-students-button" className="gap-2 rounded-md border-amber-300 text-amber-700 hover:bg-amber-50">
              <ArrowUpCircle className="h-4 w-4" strokeWidth={1.5} /> Tahun Ajaran Baru
            </Button>
          </div>
        )}
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-6">
        <div className="mb-4 flex flex-wrap gap-3">
          <div className="relative w-full max-w-sm">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" strokeWidth={1.5} />
            <Input
              placeholder="Cari nama atau NISN..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="search-student-input"
              className="pl-9"
            />
          </div>
          <div className="flex w-fit gap-1 self-start rounded-lg bg-slate-100 p-1" data-testid="students-tabs">
            <button type="button" onClick={() => setShowAlumni(false)} data-testid="students-tab-aktif"
                    className={`rounded-md px-4 py-1.5 text-sm font-medium transition-colors duration-150 ${!showAlumni ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>
              Siswa Aktif
            </button>
            <button type="button" onClick={() => setShowAlumni(true)} data-testid="students-tab-lulusan"
                    className={`rounded-md px-4 py-1.5 text-sm font-medium transition-colors duration-150 ${showAlumni ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>
              Lulusan
            </button>
          </div>
          <Select value={classFilter} onValueChange={setClassFilter}>
            <SelectTrigger className="w-[160px]" data-testid="students-class-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="semua" data-testid="students-class-semua">Semua Kelas</SelectItem>
              {classes.map((c) => (
                <SelectItem key={c} value={c} data-testid={`students-class-${c}`}>{c}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="overflow-x-auto">
          <Table data-testid="students-table">
            <TableHeader>
              <TableRow>
                <TableHead className="w-12">No</TableHead>
                <TableHead>NISN</TableHead>
                <TableHead>Nama Siswa</TableHead>
                <TableHead>Kelas</TableHead>
                <TableHead>No WA Wali</TableHead>
                {showAlumni && <TableHead>Tunggakan</TableHead>}
                {showActions && <TableHead className="text-right">Aksi</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {students === null && (
                <TableRow><TableCell colSpan={7} className="py-8 text-center text-sm text-slate-500">Memuat data...</TableCell></TableRow>
              )}
              {students !== null && filtered.length === 0 && (
                <TableRow><TableCell colSpan={7} className="py-8 text-center text-sm text-slate-500" data-testid="students-empty">{showAlumni ? "Belum ada lulusan." : "Belum ada data siswa."}</TableCell></TableRow>
              )}
              {filtered.map((s) => (
                <TableRow key={s.id} className="transition-colors duration-200 hover:bg-slate-50" data-testid={`student-row-${s.nisn}`}>
                  <TableCell className="text-sm text-slate-500" data-testid={`student-no-${s.nisn}`}>{numMap[s.id]?.no}</TableCell>
                  <TableCell className="text-sm text-slate-600">{s.nisn}</TableCell>
                  <TableCell className="text-sm font-medium text-slate-800">{s.name}</TableCell>
                  <TableCell className="text-sm text-slate-600">{s.class_name}</TableCell>
                  <TableCell className="text-sm text-slate-600">{s.parent_phone || "-"}</TableCell>
                  {showAlumni && (
                    <TableCell data-testid={`student-arrears-${s.nisn}`}>
                      {arrears[s.id] ? (
                        <span className="text-sm font-medium text-rose-600">{formatRupiah(arrears[s.id].total_sisa)}</span>
                      ) : (
                        <Badge className="rounded-full bg-emerald-50 text-emerald-700 hover:bg-emerald-50">Lunas</Badge>
                      )}
                    </TableCell>
                  )}
                  {showActions && (
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        {showAlumni ? (
                          <>
                            <Button variant="ghost" size="icon" onClick={() => openHistory(s)} data-testid={`history-student-${s.nisn}`} title="Riwayat & tunggakan">
                              <History className="h-4 w-4 text-slate-500" strokeWidth={1.5} />
                            </Button>
                            {canEdit && (
                              <Button variant="ghost" size="icon" onClick={() => setRestoring(s)} data-testid={`restore-student-${s.nisn}`} title="Kembalikan ke siswa aktif">
                                <Undo2 className="h-4 w-4 text-emerald-600" strokeWidth={1.5} />
                              </Button>
                            )}
                          </>
                        ) : (
                          <>
                            <Button variant="ghost" size="icon" onClick={() => openEdit(s)} data-testid={`edit-student-${s.nisn}`}>
                              <Pencil className="h-4 w-4 text-slate-500" strokeWidth={1.5} />
                            </Button>
                            <Button variant="ghost" size="icon" onClick={() => setDeleting(s)} data-testid={`delete-student-${s.nisn}`}>
                              <Trash2 className="h-4 w-4 text-rose-500" strokeWidth={1.5} />
                            </Button>
                          </>
                        )}
                      </div>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent aria-describedby={undefined} data-testid="student-form-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{editing ? "Ubah Data Siswa" : "Tambah Siswa Baru"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSave} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="nis">NISN</Label>
              <Input id="nis" data-testid="student-nisn-input" value={form.nisn} onChange={(e) => setForm({ ...form, nisn: e.target.value })} required placeholder="Contoh: 2024001" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="name">Nama Lengkap</Label>
              <Input id="name" data-testid="student-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required placeholder="Nama siswa" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="class_name">Kelas</Label>
              <Input id="class_name" data-testid="student-class-input" value={form.class_name} onChange={(e) => setForm({ ...form, class_name: e.target.value })} required placeholder="Contoh: 5A" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="parent_phone">No WA Wali (opsional, untuk notifikasi)</Label>
              <Input id="parent_phone" data-testid="student-phone-input" value={form.parent_phone} onChange={(e) => setForm({ ...form, parent_phone: e.target.value })} placeholder="Contoh: 081234567890" />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} data-testid="student-form-cancel">Batal</Button>
              <Button type="submit" disabled={saving} data-testid="student-form-submit" className="bg-[#0060AF] hover:bg-[#004B87]">
                {saving ? "Menyimpan..." : "Simpan"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={importOpen} onOpenChange={setImportOpen}>
        <DialogContent aria-describedby={undefined} data-testid="import-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Impor Siswa dari Excel</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleImport} className="space-y-4">
            <div className="rounded-lg bg-slate-50 px-4 py-3 text-xs leading-relaxed text-slate-600">
              File <b>.xlsx</b> dengan baris pertama berisi header kolom: <b>NISN</b>, <b>Nama</b>, <b>Kelas</b>, dan opsional <b>No WA Wali</b>.
            </div>
            <Button type="button" variant="outline" onClick={handleTemplate} data-testid="import-template-download" className="w-full gap-2 border-[#0060AF] text-[#0060AF] hover:bg-[#E5F0FA]">
              <Download className="h-4 w-4" strokeWidth={1.5} /> Unduh Format Excel
            </Button>
            <div className="space-y-2">
              <Label htmlFor="import-file">Pilih File Excel</Label>
              <Input id="import-file" type="file" accept=".xlsx" data-testid="import-file-input" onChange={(e) => setImportFile(e.target.files?.[0] || null)} required />
            </div>
            {importResult && (
              <div className="rounded-lg bg-emerald-50 px-4 py-3 text-sm text-emerald-700" data-testid="import-result">
                {importResult.imported} siswa diimpor, {importResult.skipped} dilewati.
                {importResult.errors?.length > 0 && (
                  <ul className="mt-1 list-inside list-disc text-xs text-amber-700">
                    {importResult.errors.map((err, i) => <li key={i}>{err}</li>)}
                  </ul>
                )}
              </div>
            )}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setImportOpen(false)} data-testid="import-cancel">Tutup</Button>
              <Button type="submit" disabled={importing || !importFile} data-testid="import-submit" className="bg-[#0060AF] hover:bg-[#004B87]">
                {importing ? "Mengimpor..." : "Impor Sekarang"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={promoteOpen} onOpenChange={setPromoteOpen}>
        <DialogContent aria-describedby={undefined} data-testid="promote-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Tahun Ajaran Baru — Kenaikan Kelas</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm leading-relaxed text-slate-600">
              Semua siswa aktif akan <b>naik satu tingkat</b> (mis. 4A menjadi 5A). Siswa di kelas akhir akan ditandai <b>Lulus</b> dan dinonaktifkan — riwayat keuangannya tetap tersimpan.
            </p>
            <div className="space-y-2">
              <Label>Kelas Akhir (sesuai jenjang)</Label>
              <Select value={String(finalGrade)} onValueChange={(v) => setFinalGrade(Number(v))}>
                <SelectTrigger data-testid="promote-final-grade"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="6" data-testid="promote-grade-6">Kelas 6 (SD/MI)</SelectItem>
                  <SelectItem value="9" data-testid="promote-grade-9">Kelas 9 (SMP/MTs)</SelectItem>
                  <SelectItem value="12" data-testid="promote-grade-12">Kelas 12 (SMA/MA)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setPromoteOpen(false)} data-testid="promote-cancel">Batal</Button>
            <Button onClick={handlePromote} disabled={promoting} data-testid="promote-submit" className="bg-amber-600 hover:bg-amber-700">
              {promoting ? "Memproses..." : "Jalankan Kenaikan Kelas"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!historyStudent} onOpenChange={() => { setHistoryStudent(null); setHistoryData(null); }}>
        <DialogContent aria-describedby={undefined} data-testid="alumni-history-dialog" className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading">Riwayat Keuangan — {historyStudent?.name}</DialogTitle>
          </DialogHeader>
          {!historyData ? (
            <p className="py-6 text-center text-sm text-slate-500">Memuat riwayat...</p>
          ) : (
            <div className="space-y-4">
              <div>
                <p className="text-sm font-semibold text-slate-800">Tunggakan</p>
                {historyData.tunggakan.items.length ? (
                  <ul className="mt-1 space-y-1 text-sm text-rose-600" data-testid="alumni-arrears-list">
                    {historyData.tunggakan.items.map((i, idx) => (
                      <li key={idx}>• {i.label}: {formatRupiah(i.sisa)}</li>
                    ))}
                    <li className="font-semibold">Total: {formatRupiah(historyData.tunggakan.total_sisa)}</li>
                  </ul>
                ) : (
                  <p className="mt-1 text-sm text-emerald-600" data-testid="alumni-arrears-list">Lunas — tidak ada tunggakan.</p>
                )}
              </div>
              <div>
                <p className="text-sm font-semibold text-slate-800">Pembayaran SPP</p>
                <div className="mt-1 max-h-40 overflow-y-auto">
                  {historyData.spp.length === 0 ? (
                    <p className="text-sm text-slate-500">Belum ada pembayaran SPP.</p>
                  ) : (
                    <ul className="space-y-1 text-sm text-slate-600" data-testid="alumni-spp-history">
                      {historyData.spp.map((p) => (
                        <li key={p.id}>• SPP {BULAN[p.month]} {p.year}: {formatRupiah(p.amount)}</li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
              <div>
                <p className="text-sm font-semibold text-slate-800">Tabungan</p>
                <p className="mt-1 text-sm text-slate-600" data-testid="alumni-savings-balance">
                  Saldo akhir: <b>{formatRupiah(historyData.savings.balance)}</b> · {historyData.savings.transactions.length} transaksi
                </p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!deleting}
        onOpenChange={() => setDeleting(null)}
        title="Hapus Siswa"
        description={`Yakin ingin menghapus ${deleting?.name}? Riwayat pembayarannya tetap tersimpan.`}
        onConfirm={handleDelete}
        testId="confirm-delete-student"
      />

      <ConfirmDialog
        open={!!restoring}
        onOpenChange={() => setRestoring(null)}
        title="Kembalikan ke Siswa Aktif"
        description={`Kembalikan ${restoring?.name} (kelas terakhir ${restoring?.class_name}) ke daftar siswa aktif? Seluruh riwayat keuangannya tetap tersimpan.`}
        onConfirm={handleRestore}
        testId="confirm-restore-student"
        variant="success"
        confirmLabel="Ya, Kembalikan"
      />
    </div>
  );
}
